import {
  TAG_SIGNATURE_RECORD_SEPARATOR,
  TAG_SIGNATURE_VALUE_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { useMemo, useRef } from 'react';
import { getFilterId, SearchFilter } from '../../search-filters';
import type { SearchFilterBase } from '../../search-filters/types';
import type { FilterTagAlternate } from '../../search-filters/components/filter-tags/types';
import {
  getFilterFields,
  getFilterTagsFlattened,
  getTags,
  getTagsCount,
} from '../../search-filters/components/filter-tags/utils';
import { usePinnedFiltersContext } from '../context';

export interface UseRenderPinnedFiltersResult {
  filters: SearchFilterBase[];
  tagsByFilterName: Map<string, FilterTagAlternate>;
}

function tagSignature(tags: FilterTagAlternate): string {
  const flat = getFilterTagsFlattened(tags);
  return flat
    .map(
      (t) =>
        `${t.field}=${(t.data ?? [])
          .map((d) => d?.value ?? '')
          .join(TAG_SIGNATURE_VALUE_SEPARATOR)}`
    )
    .sort()
    .join(TAG_SIGNATURE_RECORD_SEPARATOR);
}

function tagsEqual(a: FilterTagAlternate, b: FilterTagAlternate): boolean {
  if (getTagsCount(a) !== getTagsCount(b) || a.empty !== b.empty) return false;
  const flatA = getFilterTagsFlattened(a);
  const flatB = getFilterTagsFlattened(b);
  if (flatA.length !== flatB.length) return false;
  return tagSignature(a) === tagSignature(b);
}

export function useRenderPinnedFilters(): UseRenderPinnedFiltersResult {
  const {
    searchTableFilters,
    searchTablePinnedFilters,
    searchTableSearchTerm,
  } = usePinnedFiltersContext();

  const allFilterFields = useMemo(
    () =>
      Array.from(
        new Set([
          ...searchTableFilters.flatMap((f) =>
            getFilterFields(f as SearchFilter)
          ),
          ...searchTablePinnedFilters.flatMap((f) =>
            getFilterFields(f as SearchFilter)
          ),
        ])
      ),
    [searchTableFilters, searchTablePinnedFilters]
  );

  const { params } = useSearchQueryFilter({
    select: allFilterFields.length ? allFilterFields : undefined,
  });

  const prevResultRef = useRef<{
    tagsByFilterName: Map<string, FilterTagAlternate>;
  } | null>(null);

  return useMemo(() => {
    let filters: SearchFilterBase[];
    /** When building inUseFilters we already call getTags; reuse here to avoid a second call per filter. */
    const tagsCache = new Map<string, FilterTagAlternate>();

    // When searching, show all matching filters (not just pinned)
    if (searchTableSearchTerm) {
      filters = searchTableFilters;
    } else {
      // When not searching, show in-use filters + pinned filters
      const inUseFilters = searchTableFilters.filter((x) => {
        const tags = getTags(params, x as SearchFilter);
        const count = getTagsCount(tags);
        const isPinned = searchTablePinnedFilters.find(
          (y) => getFilterId(y) === getFilterId(x)
        );
        if (!!count && !isPinned) {
          tagsCache.set(x.name, tags);
          return true;
        }
        return false;
      });
      filters = [...inUseFilters, ...searchTablePinnedFilters];
    }

    const prevMap = prevResultRef.current?.tagsByFilterName;
    const tagsByFilterName = new Map<string, FilterTagAlternate>();

    for (const f of filters) {
      const cached = tagsCache.get(f.name);
      const newTags =
        cached !== undefined ? cached : getTags(params, f as SearchFilter);
      const prevTags = prevMap?.get(f.name);
      const usePrev = prevTags !== undefined && tagsEqual(prevTags, newTags);
      tagsByFilterName.set(f.name, usePrev ? prevTags : newTags);
    }

    prevResultRef.current = { tagsByFilterName };

    return { filters, tagsByFilterName };
  }, [
    searchTableFilters,
    params,
    searchTablePinnedFilters,
    searchTableSearchTerm,
  ]);
}
